// ComputerAgent page
//
// Purpose
// - Launch a sandboxed Linux desktop session (via backend /cua/start-session)
// - Embed the live desktop (noVNC) in an iframe so the user can watch
// - Let the user chat a task to the Computer-Using Agent (CUA)
// - Send the prompt to the backend (/cua/agent/execute) which runs the CUA loop
// - Display the agent's textual reply in the chat area
//
// Key interactions
// - Start Session button → POST /cua/start-session → returns sessionId + noVNC URL
// - Send message → POST /cua/agent/execute with { sessionId, prompt }
// - On unmount/close → POST /cua/stop-session to release resources
//
// UX notes
// - Disables input while a request is in-flight (isSending)
// - Auto-scrolls chat to the latest message
// - Enter to send (Shift+Enter for newline)

import React, { useState, useRef, useEffect } from "react";
import Layout from "../components/Layout";
import config from "../config";
import "./ComputerAgent.css";
import "./Chat.css";

function ComputerAgent() {
  // --- State: session, chat, and UI ---
  const [wsUrl, setWsUrl] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  // Chat state for right pane
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  // --- Start a new desktop session (backend assigns a container + noVNC URL) ---
  const startSession = async () => {
    const res = await fetch(`${config.api.baseUrl}/cua/start-session`, {
      method: "POST",
    });
    const data = await res.json();
    if (!data.websocketUrl) {
      alert(data.error || "Failed to start session.");
      return;
    }
    setSessionId(data.sessionId);
    setWsUrl(data.websocketUrl);
  };

  // --- Cleanup on unmount or tab close (stop-session to free resources) ---
  useEffect(() => {
    const stopSession = () => {
      if (sessionId) {
        fetch(`${config.api.baseUrl}/cua/stop-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId }),
        });
      }
    };
    window.addEventListener("beforeunload", stopSession);
    return () => {
      stopSession();
      window.removeEventListener("beforeunload", stopSession);
    };
  }, [sessionId]);

  // --- Auto-scroll to the newest message when the chat updates ---
  useEffect(() => {
    if (messagesEndRef.current) {
      try {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      } catch (e) {
        // no-op
      }
    }
  }, [messages]);

  // --- Send user prompt to the backend agent and append the agent reply ---
  const handleSendMessage = async () => {
    if (!inputText.trim()) return;
    if (isSending) return;
    if (!sessionId) {
      alert("Please start a session first.");
      return;
    }
    setIsSending(true);
    const userMessage = {
      id: Date.now(),
      role: "user",
      content: inputText,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    const prompt = inputText;
    setInputText("");
    if (textareaRef.current) textareaRef.current.style.height = "60px";
    try {
      const res = await fetch(`${config.api.baseUrl}/cua/agent/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, prompt }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 2,
            role: "assistant",
            content: data.error || "Agent failed to respond.",
            timestamp: new Date().toISOString(),
          },
        ]);
        return;
      }
      const reply = data.assistantMessage || "(No response)";
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 3,
          role: "assistant",
          content: reply,
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 4,
          role: "assistant",
          content: "Network error while contacting agent.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  // --- Textarea change handler: autosize within min height ---
  const handleInputChange = (e) => {
    setInputText(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = "60px";
      if (textareaRef.current.scrollHeight > textareaRef.current.clientHeight) {
        textareaRef.current.style.height = `${Math.max(
          60,
          textareaRef.current.scrollHeight
        )}px`;
      }
    }
  };

  // --- Enter to send; Shift+Enter for newline ---
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Layout>
      <div className="computer-agent-container">
        {/* Left pane: Virtual Desktop */}
        <div className="computer-agent-left">
          {!wsUrl ? (
            <button className="computer-agent-start-btn" onClick={startSession}>
              Start Session
            </button>
          ) : (
            <div className="computer-agent-iframe-wrapper">
              <iframe
                src={wsUrl}
                title="Virtual Desktop"
                className="computer-agent-iframe"
                allowFullScreen
              />
            </div>
          )}
        </div>
        {/* Right pane: Chat area */}
        <div className="computer-agent-right">
          <div className="chat-div">
            <div className="chat-messages">
              {messages.length === 0 ? (
                <div className="empty-chat-fullheight">
                  <div className="welcome-container">
                    <p className="empty-chat-description">
                      Start a conversation with the Computer Agent.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`chat-message ${message.role}`}
                    >
                      <div className="message-wrapper">
                        <div className="message-content">{message.content}</div>
                        <div className="message-timestamp">
                          {formatTime(message.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
            <div className="chat-input-wrapper">
              <div className="chat-input-container">
                <textarea
                  ref={textareaRef}
                  className="chat-input"
                  placeholder={"Type a message..."}
                  value={inputText}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  disabled={isSending}
                />
                <div className="buttons-container">
                  <button
                    className="circular-button send-button"
                    disabled={!inputText.trim() || isSending}
                    onClick={handleSendMessage}
                  >
                    {isSending ? "Sending..." : "Send"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ComputerAgent;
